import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { getArticlesForRoute } from './beacon-articles';
import posthog from './posthog';

declare global {
  interface Window {
    Beacon: (...args: unknown[]) => void;
  }
}

function suggestForPath(pathname: string) {
  if (typeof window === 'undefined') return;
  window.Beacon('event', {
    title: document.title,
    type: 'page-viewed',
    url: document.location.href,
  });
  const articles = getArticlesForRoute(pathname);

  if (articles.length > 0) {
    window.Beacon('suggest', articles);
  } else {
    window.Beacon('suggest');
  }
}

export function useBeacon({
  church,
  layout,
  plan,
  user,
}: {
  layout?: string;
  user?: WithAuthUser;
  church?: WithAuthChurch;
  plan?: string;
}) {
  const router = useRouter();
  const isAdmin = layout?.includes('ADMIN') ?? layout === 'AUTH';

  // Identify user, configure branding, set session data, and suggest on load
  useEffect(() => {
    if (!isAdmin || typeof window === 'undefined') return;

    const init = () => {
      window.Beacon('config', { color: '#7B2CFF' });

      if (user) {
        const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

        window.Beacon('identify', { email: user.email, name });
        window.Beacon('session-data', {
          Church: church?.name ?? '',
          ...(plan ? { Plan: plan } : {}),
          Page: window.location.pathname,
        });
      }

      suggestForPath(window.location.pathname);
    };

    // window.Beacon is always defined as a queue shim by the inline loader script,
    // so calls are safe immediately and replayed once the full JS loads.
    init();
    window.addEventListener('load', init, { once: true });

    return () => window.removeEventListener('load', init);
  }, [isAdmin, user, church, plan]);

  // Update suggestions on every client-side navigation
  useEffect(() => {
    if (!isAdmin) return;

    const handleRouteChange = (url: string) => {
      const pathname = url.split('?')[0];

      suggestForPath(pathname);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [isAdmin, router.events]);

  // Track Beacon interactions in PostHog
  useEffect(() => {
    if (!isAdmin || typeof window === 'undefined') return;

    const onArticleViewed = ({ id }: { id: string }) => {
      posthog?.capture('beacon_article_viewed', { article_id: id });
    };
    const onEmailSent = ({
      email,
    }: {
      email: string;
      name: string;
      text: string;
    }) => {
      posthog?.capture('beacon_email_sent', { email });
    };
    const onChatStarted = ({
      email,
    }: {
      email: string;
      name: string;
      subject: string;
    }) => {
      posthog?.capture('beacon_chat_started', { email });
    };
    const onSearch = ({ query }: { query: string }) => {
      posthog?.capture('beacon_search', { query });
    };

    window.Beacon('on', 'article-viewed', onArticleViewed);
    window.Beacon('on', 'email-sent', onEmailSent);
    window.Beacon('on', 'chat-started', onChatStarted);
    window.Beacon('on', 'search', onSearch);

    return () => {
      window.Beacon('off', 'article-viewed', onArticleViewed);
      window.Beacon('off', 'email-sent', onEmailSent);
      window.Beacon('off', 'chat-started', onChatStarted);
      window.Beacon('off', 'search', onSearch);
    };
  }, [isAdmin]);
}

export function beaconLogout() {
  if (typeof window !== 'undefined') {
    window.Beacon('logout');
  }
}
